import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { DEFAULT_THEME, THEME_OPTIONS, getThemeStyles } from "../themeConfig";
import type { ThemeKey } from "@sparklab/types";

const THEME_LABELS: Record<ThemeKey, string> = {
  bright: "霓虹奇点",
  sunset: "落日余晖",
  mint: "薄荷绿洲",
  rose: "玫瑰星云",
  aurora: "极光幻境",
  midnight: "午夜静谧",
};

export default function ProfilePage() {
  const { user, updateTheme, changePassword, logout } = useAuth();
  const navigate = useNavigate();
  const themeKey = user?.theme ?? DEFAULT_THEME;
  const t = getThemeStyles(themeKey);
  const [selectedTheme, setSelectedTheme] = useState<ThemeKey>(themeKey);
  const [themeSaving, setThemeSaving] = useState(false);
  const [themeMessage, setThemeMessage] = useState<string | null>(null);
  const [themeError, setThemeError] = useState<string | null>(null);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const handleThemeSave = async () => {
    if (!user) return;
    setThemeSaving(true);
    setThemeMessage(null);
    setThemeError(null);
    try {
      await updateTheme(selectedTheme);
      setThemeMessage("主题已更新 🎨");
    } catch (error) {
      setThemeError(error instanceof Error ? error.message : "更新主题失败");
    } finally {
      setThemeSaving(false);
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMessage(null);
    setPasswordError(null);

    if (newPassword.length < 6) {
      setPasswordError("新密码长度至少为6位");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("两次输入的密码不一致");
      return;
    }

    setPasswordSaving(true);
    try {
      await changePassword({ current_password: currentPassword, new_password: newPassword });
      setPasswordMessage("密码已更新 ✅");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      setPasswordError(error instanceof Error ? error.message : "修改密码失败");
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className={`min-h-screen ${t.bg} text-white p-6 font-mono transition-all duration-300`}>
      <div className="flex justify-between items-center mb-6 gap-3 flex-wrap">
        <button
          onClick={() => navigate("/")}
          className={`px-4 py-2 rounded-xl border-2 font-bold ${t.button1} hover:scale-105 transition`}
        >
          ← 返回首页
        </button>
        <div className="flex gap-3">
          <button
            onClick={() => navigate("/my-sparks")}
            className={`px-4 py-2 rounded-xl border-2 font-bold ${t.button2} hover:scale-105 transition`}
          >
            我的作品
          </button>
          <button
            onClick={handleLogout}
            className={`px-4 py-2 rounded-xl border-2 font-bold ${t.button3} hover:scale-105 transition`}
          >
            退出登录
          </button>
        </div>
      </div>

      <header className="text-center mb-10">
        <h1 className={`text-4xl font-bold tracking-wide drop-shadow-lg ${t.header}`}>👤 我的资料</h1>
        <p className={`text-lg mt-2 ${t.subheader}`}>定制你的主题风格，并保护账号安全</p>
      </header>

      <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className={`p-6 rounded-3xl shadow-xl border-4 ${t.card}`}>
          <h2 className={`text-2xl font-bold mb-4 ${t.subheader}`}>🎨 主题偏好</h2>
          <p className="text-sm opacity-80 mb-4">选择你喜欢的主题，让 SparkLab 为你保持这份氛围。</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            {THEME_OPTIONS.map((option) => {
              const optionTheme = getThemeStyles(option);
              const isSelected = selectedTheme === option;
              return (
                <button
                  key={option}
                  onClick={() => setSelectedTheme(option)}
                  className={`rounded-2xl border-2 text-left transition flex flex-col gap-3 p-3 ${
                    isSelected
                      ? "bg-white text-black border-white shadow-lg"
                      : "bg-black/20 text-white hover:bg-white/10 border-white/40"
                  }`}
                >
                  <div className={`rounded-2xl border-2 h-20 flex items-center justify-center ${optionTheme.card}`}>
                    <span className="font-bold text-base tracking-wide">{THEME_LABELS[option]}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className={`flex-1 h-3 rounded-full border ${optionTheme.button1}`} />
                    <span className={`flex-1 h-3 rounded-full border ${optionTheme.button2}`} />
                    <span className={`flex-1 h-3 rounded-full border ${optionTheme.button3}`} />
                  </div>
                  <p className="text-xs uppercase opacity-70 tracking-wide">{option}</p>
                </button>
              );
            })}
          </div>
          <button
            onClick={handleThemeSave}
            disabled={themeSaving || selectedTheme === themeKey}
            className={`w-full py-3 rounded-xl border-2 ${t.button1} text-white font-bold hover:scale-105 transition disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {themeSaving ? "保存中..." : selectedTheme === themeKey ? "已是当前主题" : "保存主题"}
          </button>
          {themeMessage && <p className="mt-3 text-green-200 text-sm">{themeMessage}</p>}
          {themeError && <p className="mt-3 text-red-200 text-sm">{themeError}</p>}
        </section>

        <section className={`p-6 rounded-3xl shadow-xl border-4 ${t.card}`}>
          <h2 className={`text-2xl font-bold mb-4 ${t.subheader}`}>🔐 修改密码</h2>
          <form onSubmit={handlePasswordUpdate} className="space-y-4">
            <div>
              <label className={`block mb-2 ${t.subheader} font-bold`}>当前密码</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className={`w-full px-4 py-3 rounded-xl border-2 ${t.input} focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                placeholder="请输入当前密码"
                required
              />
            </div>
            <div>
              <label className={`block mb-2 ${t.subheader} font-bold`}>新密码</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={`w-full px-4 py-3 rounded-xl border-2 ${t.input} focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                placeholder="至少 6 位"
                required
                minLength={6}
              />
            </div>
            <div>
              <label className={`block mb-2 ${t.subheader} font-bold`}>确认新密码</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`w-full px-4 py-3 rounded-xl border-2 ${t.input} focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                placeholder="再次输入新密码"
                required
                minLength={6}
              />
            </div>
            {passwordMessage && <p className="text-green-200 text-sm">{passwordMessage}</p>}
            {passwordError && <p className="text-red-200 text-sm">{passwordError}</p>}
            <button
              type="submit"
              disabled={passwordSaving}
              className={`w-full py-3 rounded-xl border-2 ${t.button1} text-white font-bold hover:scale-105 transition disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {passwordSaving ? "修改中..." : "更新密码"}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}

